import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import { InstanceApiFactory } from '../api/instanceApi';
import type { InstanceRow } from '../instanceTypes';

interface UseGenerateKeyOptions {
  /** Called after a key is successfully generated (e.g. to refresh the list so the key-status column updates). */
  onSuccess?: () => void;
}

interface UseGenerateKeyResult {
  generatingKeyId: string | null;
  generateKey: (row: InstanceRow) => Promise<void>;
}

export function useGenerateKey({ onSuccess }: UseGenerateKeyOptions = {}): UseGenerateKeyResult {
  const { t } = useTranslation('instances');
  const [client] = useFetch();
  const api = useMemo(() => InstanceApiFactory(client), [client]);
  const { notifySuccess, notifyError } = useNotification();

  const [generatingKeyId, setGeneratingKeyId] = useState<string | null>(null);
  const ctrlRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      ctrlRef.current?.abort();
    };
  }, []);

  const generateKey = useCallback(
    async (row: InstanceRow) => {
      ctrlRef.current?.abort();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;

      setGeneratingKeyId(row.id);
      try {
        await api.generateKey(row.id, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        notifySuccess(t('generateKey.success'));
        onSuccess?.();
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        notifyError(err);
      } finally {
        if (!ctrl.signal.aborted) {
          setGeneratingKeyId(null);
        }
      }
    },
    [api, notifySuccess, notifyError, t, onSuccess],
  );

  return { generatingKeyId, generateKey };
}
